/**
 * PublishPreviewModal — Pre-publish review (Session #149).
 *
 * Fetches GET /api/profile/publish/preview, shows what's about to become
 * public (diff for republish, full content summary for first publish), and
 * gates the actual POST /api/profile/publish behind an explicit approval.
 */

import { useEffect, useState } from 'preact/hooks';
import { profileApi, type PublishPreview, type Profile, type EntityChange } from '../api/profile';

interface Props {
	profile: Profile;
	getToken: () => Promise<string | null>;
	onClose: () => void;
	onPublished: (result: { slug: string }) => void;
}

export function PublishPreviewModal({ profile, getToken, onClose, onPublished }: Props) {
	const [preview, setPreview] = useState<PublishPreview | null>(null);
	const [loading, setLoading] = useState(true);
	const [publishing, setPublishing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		async function load() {
			try {
				const token = await getToken();
				if (!token || cancelled) return;
				const p = await profileApi.publishPreview(token);
				if (!cancelled) setPreview(p);
			} catch (err) {
				if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load publish preview');
			} finally {
				if (!cancelled) setLoading(false);
			}
		}
		load();
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function handlePublish() {
		if (publishing) return;
		try {
			setPublishing(true);
			setError(null);
			const token = await getToken();
			if (!token) return;
			const result = await profileApi.publish(token);
			onPublished({ slug: result.slug });
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to publish');
			setPublishing(false);
		}
	}

	const isFirstPublish = preview && !preview.hasActiveSnapshot;
	const hasChanges = preview && preview.changes.length > 0;
	const companiesNeedingEnrichment = preview?.sideEffects.companiesNeedingEnrichment || [];

	return (
		<div class="preview-overlay" onClick={onClose}>
			<div class="preview-modal" onClick={(e: Event) => e.stopPropagation()}>
				<header class="preview-header">
					<h2 class="preview-title">
						{isFirstPublish ? 'Publish your profile' : 'Review before publishing'}
					</h2>
					{preview && (
						<p class="preview-url">
							<span class="preview-url__label">Destination</span>
							<span class="preview-url__value">{preview.publishUrl}</span>
						</p>
					)}
				</header>

				<div class="preview-body">
					{loading && (
						<div class="preview-loading">
							<span class="spinner" /> Building preview…
						</div>
					)}

					{!loading && error && !preview && (
						<div class="preview-error">{error}</div>
					)}

					{preview && !preview.canPublish && (
						<div class="preview-floor">
							<p class="preview-floor__title">Not enough in your record yet</p>
							{preview.floorReasons.length > 0 && (
								<ul class="preview-floor__reasons">
									{preview.floorReasons.map((r, i) => <li key={i}>{r}</li>)}
								</ul>
							)}
							<p class="preview-floor__hint">Continue a record session, then come back to publish.</p>
						</div>
					)}

					{preview && preview.canPublish && isFirstPublish && (
						<FirstPublishSummary preview={preview} profile={profile} />
					)}

					{preview && preview.canPublish && !isFirstPublish && hasChanges && (
						<ChangesSection changes={preview.changes} />
					)}

					{preview && preview.canPublish && !isFirstPublish && !hasChanges && (
						<div class="preview-no-changes">
							<p class="preview-no-changes__title">No content changes since last publish.</p>
							<p class="preview-no-changes__hint">
								Publishing will regenerate your narrative{companiesNeedingEnrichment.length > 0 ? ' and refresh company intelligence' : ''}.
							</p>
						</div>
					)}

					{preview && preview.canPublish && (
						<SideEffectsSection
							narrativeWillRegenerate={preview.sideEffects.narrativeWillRegenerate}
							companiesNeedingEnrichment={companiesNeedingEnrichment}
						/>
					)}

					{error && preview && (
						<div class="preview-error">{error}</div>
					)}
				</div>

				<footer class="preview-actions">
					<button
						class="btn btn-ghost"
						onClick={onClose}
						disabled={publishing}
					>
						Cancel
					</button>
					<button
						class="btn btn-primary"
						onClick={handlePublish}
						disabled={loading || publishing || !preview?.canPublish}
					>
						{publishing ? <><span class="spinner" /> Publishing…</> : (isFirstPublish ? 'Publish' : 'Publish update')}
					</button>
				</footer>
				<style>{previewStyles}</style>
			</div>
		</div>
	);
}

function FirstPublishSummary({ preview, profile }: { preview: PublishPreview; profile: Profile }) {
	const s = preview.nextSnapshot;
	return (
		<div class="preview-first">
			<p class="preview-first__lead">This is what will become public on your first publish.</p>
			<dl class="preview-summary-list">
				<div class="preview-summary-row">
					<dt>Claims</dt>
					<dd>{s.claimCount}</dd>
				</div>
				<div class="preview-summary-row">
					<dt>Work history</dt>
					<dd>{s.workHistoryCount} {s.workHistoryCount === 1 ? 'company' : 'companies'}</dd>
				</div>
				<div class="preview-summary-row">
					<dt>Projects</dt>
					<dd>{s.projectCount}</dd>
				</div>
				{s.peopleCount > 0 && (
					<div class="preview-summary-row">
						<dt>People</dt>
						<dd>{s.peopleCount}</dd>
					</div>
				)}
				{s.eventCount > 0 && (
					<div class="preview-summary-row">
						<dt>Events</dt>
						<dd>{s.eventCount}</dd>
					</div>
				)}
			</dl>

			<div class="preview-checklist">
				<p class="preview-checklist__title">Profile setup</p>
				<ChecklistItem ok={profile.chatEnabled} label={`Chat widget ${profile.chatEnabled ? 'enabled' : 'disabled'}`} />
				{profile.resumeUrl && <ChecklistItem ok label="Resume link" />}
				{profile.linkedinUrl && <ChecklistItem ok label="LinkedIn" />}
				{profile.portfolioUrl && <ChecklistItem ok label="Portfolio" />}
				{!profile.resumeUrl && !profile.linkedinUrl && !profile.portfolioUrl && (
					<ChecklistItem ok={false} label="No external links set" />
				)}
			</div>
		</div>
	);
}

function ChecklistItem({ ok, label }: { ok: boolean; label: string }) {
	return (
		<div class={`preview-checklist-item ${ok ? 'preview-checklist-item--ok' : 'preview-checklist-item--missing'}`}>
			<span class="preview-checklist-mark">{ok ? '\u2713' : '\u00d7'}</span>
			<span>{label}</span>
		</div>
	);
}

function ChangesSection({ changes }: { changes: EntityChange[] }) {
	const grouped = groupChanges(changes);
	const totalAdded = changes.filter(c => c.kind === 'added').length;
	const totalRemoved = changes.filter(c => c.kind === 'removed').length;
	const totalModified = changes.filter(c => c.kind === 'modified').length;

	return (
		<div class="preview-changes">
			<p class="preview-changes__summary">
				{totalAdded > 0 && <span class="preview-count preview-count--added">{totalAdded} added</span>}
				{totalModified > 0 && <span class="preview-count preview-count--modified">{totalModified} changed</span>}
				{totalRemoved > 0 && <span class="preview-count preview-count--removed">{totalRemoved} removed</span>}
			</p>
			{grouped.map(group => (
				<section key={group.label} class="preview-group">
					<h3 class="preview-group__label">{group.label}</h3>
					<ul class="preview-group__list">
						{group.items.map(item => (
							<li key={`${item.entityType}-${item.displayName}-${item.kind}`} class={`preview-item preview-item--${item.kind}`}>
								<span class="preview-item__kind">{kindLabel(item.kind)}</span>
								<span class="preview-item__name">{item.displayName}</span>
								{item.changes && item.changes.length > 0 && (
									<ul class="preview-item__detail">
										{item.changes.map((c, i) => <li key={i}>{c}</li>)}
									</ul>
								)}
							</li>
						))}
					</ul>
				</section>
			))}
		</div>
	);
}

interface ChangeGroup { label: string; items: EntityChange[] }

function groupChanges(changes: EntityChange[]): ChangeGroup[] {
	const order: { type: EntityChange['entityType']; label: string }[] = [
		{ type: 'identity', label: 'Identity' },
		{ type: 'work', label: 'Work history' },
		{ type: 'project', label: 'Projects' },
		{ type: 'person', label: 'People' },
		{ type: 'event', label: 'Events' },
		{ type: 'skill', label: 'Skills' },
	];
	const groups: ChangeGroup[] = [];
	for (const { type, label } of order) {
		const items = changes.filter(c => c.entityType === type);
		if (items.length > 0) groups.push({ label, items });
	}
	return groups;
}

function kindLabel(kind: EntityChange['kind']): string {
	if (kind === 'added') return '+ Added';
	if (kind === 'removed') return '− Removed';
	return '• Changed';
}

function SideEffectsSection({
	narrativeWillRegenerate,
	companiesNeedingEnrichment,
}: {
	narrativeWillRegenerate: boolean;
	companiesNeedingEnrichment: string[];
}) {
	if (!narrativeWillRegenerate && companiesNeedingEnrichment.length === 0) return null;
	return (
		<aside class="preview-sideeffects">
			<p class="preview-sideeffects__title">Also on publish</p>
			<ul>
				{narrativeWillRegenerate && <li>Your professional narrative will regenerate.</li>}
				{companiesNeedingEnrichment.length > 0 && (
					<li>
						{companiesNeedingEnrichment.length === 1
							? `BETA intelligence will load for ${companiesNeedingEnrichment[0]}.`
							: `BETA intelligence will load for ${companiesNeedingEnrichment.length} companies (${companiesNeedingEnrichment.slice(0, 3).join(', ')}${companiesNeedingEnrichment.length > 3 ? ', …' : ''}).`}
					</li>
				)}
			</ul>
		</aside>
	);
}

const previewStyles = `
	.preview-overlay {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 1.5rem;
		/* Above GuideCompanion (z-index: 200) so the quip doesn't bleed over the dim backdrop */
		z-index: 300;
	}

	.preview-modal {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 4px;
		width: 100%;
		max-width: 640px;
		max-height: min(90vh, 780px);
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	.preview-header {
		padding: 1.25rem 1.5rem 0.75rem;
		border-bottom: 1px solid var(--color-border);
	}

	.preview-title {
		font-family: var(--font-display);
		font-weight: 300;
		font-size: 1.375rem;
		margin: 0 0 0.5rem;
		color: var(--color-text);
	}

	.preview-url {
		display: flex;
		align-items: baseline;
		gap: 0.5rem;
		margin: 0 0 0.75rem;
		font-size: 0.8125rem;
	}

	.preview-url__label {
		color: var(--color-text-subtle);
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.preview-url__value {
		color: var(--color-text);
		font-family: var(--font-mono);
	}

	.preview-body {
		flex: 1;
		overflow-y: auto;
		padding: 1rem 1.5rem;
	}

	.preview-loading,
	.preview-error {
		padding: 1.5rem 0;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		font-size: 0.875rem;
		color: var(--color-text-muted);
	}

	.preview-error {
		color: var(--color-danger, #dc2626);
	}

	.preview-floor {
		padding: 1rem;
		background: var(--color-bg);
		border: 1px dashed var(--color-border);
		border-radius: 2px;
	}

	.preview-floor__title {
		font-weight: 500;
		margin: 0 0 0.5rem;
		color: var(--color-text);
	}

	.preview-floor__reasons {
		margin: 0.5rem 0;
		padding-left: 1.25rem;
		color: var(--color-text-muted);
		font-size: 0.875rem;
	}

	.preview-floor__hint {
		margin: 0.75rem 0 0;
		color: var(--color-text-subtle);
		font-size: 0.8125rem;
	}

	.preview-first__lead {
		margin: 0 0 0.75rem;
		color: var(--color-text-muted);
		font-size: 0.9375rem;
	}

	.preview-summary-list {
		margin: 0 0 1rem;
		display: grid;
		gap: 0.375rem;
	}

	.preview-summary-row {
		display: flex;
		justify-content: space-between;
		padding: 0.375rem 0;
		border-bottom: 1px solid var(--color-border);
		font-size: 0.875rem;
	}

	.preview-summary-row dt {
		color: var(--color-text-muted);
		margin: 0;
	}

	.preview-summary-row dd {
		margin: 0;
		color: var(--color-text);
		font-family: var(--font-mono);
		font-size: 0.8125rem;
	}

	.preview-checklist {
		margin-top: 1rem;
		padding-top: 0.75rem;
		border-top: 1px solid var(--color-border);
	}

	.preview-checklist__title {
		margin: 0 0 0.5rem;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
	}

	.preview-checklist-item {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.25rem 0;
		font-size: 0.875rem;
	}

	.preview-checklist-mark {
		display: inline-block;
		width: 16px;
		text-align: center;
		font-family: var(--font-mono);
	}

	.preview-checklist-item--ok .preview-checklist-mark { color: var(--color-success, #22c55e); }
	.preview-checklist-item--missing { color: var(--color-text-subtle); }
	.preview-checklist-item--missing .preview-checklist-mark { color: var(--color-text-subtle); }

	.preview-no-changes {
		padding: 0.875rem 1rem;
		background: var(--color-bg);
		border: 1px dashed var(--color-border);
		border-radius: 2px;
	}

	.preview-no-changes__title {
		margin: 0 0 0.375rem;
		color: var(--color-text);
		font-size: 0.9375rem;
	}

	.preview-no-changes__hint {
		margin: 0;
		color: var(--color-text-muted);
		font-size: 0.8125rem;
	}

	.preview-changes__summary {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
		margin: 0 0 0.875rem;
	}

	.preview-count {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		padding: 0.125rem 0.5rem;
		border-radius: 2px;
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		color: var(--color-text-muted);
	}

	.preview-count--added { color: var(--color-success, #22c55e); border-color: color-mix(in srgb, var(--color-success, #22c55e) 35%, var(--color-border)); }
	.preview-count--removed { color: var(--color-danger, #dc2626); border-color: color-mix(in srgb, var(--color-danger, #dc2626) 35%, var(--color-border)); }
	.preview-count--modified { color: var(--color-warning, #f5a623); border-color: color-mix(in srgb, var(--color-warning, #f5a623) 35%, var(--color-border)); }

	.preview-group {
		margin-bottom: 1rem;
	}

	.preview-group__label {
		margin: 0 0 0.375rem;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
	}

	.preview-group__list {
		margin: 0;
		padding: 0;
		list-style: none;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.preview-item {
		padding: 0.5rem 0.75rem;
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-left-width: 3px;
		border-radius: 2px;
		font-size: 0.875rem;
	}

	.preview-item--added { border-left-color: var(--color-success, #22c55e); }
	.preview-item--removed { border-left-color: var(--color-danger, #dc2626); }
	.preview-item--modified { border-left-color: var(--color-warning, #f5a623); }

	.preview-item__kind {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
		margin-right: 0.5rem;
	}

	.preview-item--added .preview-item__kind { color: var(--color-success, #22c55e); }
	.preview-item--removed .preview-item__kind { color: var(--color-danger, #dc2626); }
	.preview-item--modified .preview-item__kind { color: var(--color-warning, #f5a623); }

	.preview-item__name {
		color: var(--color-text);
		font-weight: 500;
	}

	.preview-item__detail {
		margin: 0.375rem 0 0;
		padding-left: 1.25rem;
		color: var(--color-text-muted);
		font-size: 0.8125rem;
		line-height: 1.4;
		overflow-wrap: anywhere;
	}

	.preview-sideeffects {
		margin-top: 1rem;
		padding: 0.75rem 0.875rem;
		background: var(--color-bg);
		border-radius: 2px;
	}

	.preview-sideeffects__title {
		margin: 0 0 0.375rem;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--color-text-subtle);
	}

	.preview-sideeffects ul {
		margin: 0;
		padding-left: 1rem;
		color: var(--color-text-muted);
		font-size: 0.8125rem;
	}

	.preview-actions {
		padding: 0.875rem 1.5rem;
		border-top: 1px solid var(--color-border);
		display: flex;
		justify-content: flex-end;
		gap: 0.75rem;
	}

	@media (max-width: 639px) {
		.preview-overlay { padding: 0.75rem; align-items: flex-end; }
		.preview-modal { max-height: 95vh; border-radius: 4px 4px 0 0; }
		.preview-header, .preview-body, .preview-actions { padding-left: 1rem; padding-right: 1rem; }
	}
`;
